import { useState, useEffect, useContext } from 'react';
import {
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    CircularProgress,
    Alert,
    IconButton,
    Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation, TFunction } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CarSharingInvoicesResponse } from '../../types';
import { getInvoices, downloadInvoice, getCarsharingClient } from '../../api/service/customerbaseApi';
import { CarsharingRedirectParams, redirectToCarsharing } from '../../../../util/carsharingService';
import { GridColDef } from '@mui/x-data-grid';
import CarSharingDataGrid from './CarSharingDataGrid';
import { useCarSharingContext } from './CarSharingContext';
import { GlobalContext } from '../../../../util/state/GlobalContextProvider';
import { setSnackbar } from '../../../../util/state/globalAction';
import { snackbarTypes } from '../../../../util/state/globalVariables';

const monthMap: { [key: string]: string } = {
    'gen': 'Jan', 'feb': 'Feb', 'mar': 'Mar', 'apr': 'Apr', 'mag': 'May', 'giu': 'Jun',
    'lug': 'Jul', 'ago': 'Aug', 'set': 'Sep', 'ott': 'Oct', 'nov': 'Nov', 'dic': 'Dec'
};

const dateValueFormatter = ({ value }: { value: string | number | null | undefined }) => {
    if (!value) return '-';

    let date: Date;

    if (typeof value === 'number') {
        date = new Date(value);
    }

    else if (!isNaN(Number(value))) {
        date = new Date(Number(value));
    }

    else if (!isNaN(Date.parse(value))) {
        date = new Date(value);
    }

    else {
        const parts = value.split(' ');
        if (parts.length === 3) {
            const [day, monthAbbr, year] = parts;
            const month = monthMap[monthAbbr.toLowerCase()];
            if (month) {
                const dateString = `${day} ${month} ${year}`;
                const parsed = new Date(dateString);
                if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString('it-IT');
            }
        }
        return value;
    }
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('it-IT');
};

const getColumns = (t: TFunction, onDownload: (invoiceIdentifier: string, invoiceNumber: string) => void): GridColDef[] => [
    {
        field: 'number',
        headerName: t('csInvoices.headers.number'),
        flex: 1,
        minWidth: 175,
    },
    {
        field: 'firstName',
        headerName: t('csInvoices.headers.firstName'),
        flex: 1,
        minWidth: 145,
    },
    {
        field: 'lastName',
        headerName: t('csInvoices.headers.lastName'),
        flex: 1,
        minWidth: 145,
    },
    {
        field: 'amount',
        headerName: t('csInvoices.headers.amount'),
        flex: 1,
        minWidth: 90,
        valueFormatter: ({ value }) => {
            if (value == null || isNaN(Number(value))) return '-';
            return Number(value).toFixed(2);
        },
    },
    {
        field: 'referenceTime',
        headerName: t('csInvoices.headers.date'),
        flex: 1,
        minWidth: 100,
        valueFormatter: dateValueFormatter,
    },
    {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 50,
        align: 'center',
        renderCell: (params) => (
            <IconButton
                onClick={() => onDownload(params.row.identifier, params.row.number)}
                color="primary"
                size="small"
            >
                <DownloadIcon fontSize="inherit" />
            </IconButton>
        ),
    },
];

export default function CustomerBaseCarSharingInvoices() {
    const { csCustomerId, language, market } = useCarSharingContext();
    const { t } = useTranslation('customerbase');
    const { dispatchGlobalData } = useContext(GlobalContext);
    const [expanded, setExpanded] = useState(false);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);

    useEffect(() => {
        if (expanded && !hasBeenOpened) {
            setHasBeenOpened(true);
        }
    }, [expanded, hasBeenOpened]);

    const handleRedirect = async () => {
        if (!csCustomerId) return;

        const params: CarsharingRedirectParams = {
            lang: language || 'it',
            type: 'CUSTOMER',
            section: 'INVOICES',
            id: String(csCustomerId)
        };

        try {
            await redirectToCarsharing(params);
        } catch (error) {
            console.error('Redirect failed:', error);
        }
    };

    const handleDownload = async (invoiceIdentifier: string, invoiceNumber: string) => {
        if (!market) return;
        try {
            const blob = await downloadInvoice(invoiceIdentifier, market);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${invoiceNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            let handled = false;

            if (error.response && error.response.status === 500 && error.response.data instanceof Blob) {
                try {
                    const errorDataText = await error.response.data.text();
                    const errorJson = JSON.parse(errorDataText);

                    if (errorJson.errorMessage?.includes('406')) {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t('csInvoices.downloadError406'),
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoClose: true,
                                }
                            }
                        });
                        handled = true;
                    }
                } catch (jsonError) {
                    console.error('Failed to parse error response JSON while downloading invoice:', jsonError);
                }
            }

            if (!handled) {
                console.error('Download failed with an unexpected error:', error);
            }
        }
    };


    const {
        data: invoiceData,
        isError,
        error,
        isLoading
    } = useQuery<CarSharingInvoicesResponse>(
        ['invoices', csCustomerId, language],
        () => getInvoices(String(csCustomerId!), getCarsharingClient(), 0, language as 'it' | 'en' | 'fr'),
        {
            enabled: !!csCustomerId && hasBeenOpened,
        }
    );

    const columns = getColumns(t, handleDownload);

    const validInvoices = (invoiceData?.rows || []).filter(r => !!r.identifier);
    const invoicesWithId = validInvoices.map(r => ({ ...r, id: r.identifier }));

    return (
        <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography sx={{ fontWeight: 500 }}>
                        {t("tabs.accordionsLabels.csInvoices")}
                    </Typography>
                    {expanded && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                void handleRedirect();
                            }}
                            disabled={!csCustomerId}
                            color="secondary"
                        >
                            <LinkIcon />
                        </IconButton>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {isLoading ? (
                            <CircularProgress />
                        ) : isError ? (
                            <Alert severity="error">{(error as Error)?.message}</Alert>
                        ) : (
                            <CarSharingDataGrid
                                rows={invoicesWithId}
                                columns={columns}
                            />
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}
